import type { CompactResourceDescriptor } from "@/desktop/compact-resource-events";
import type { WorkToastAppRef, WorkToastItem, WorkToastOpenTarget } from "@/stores/work-toast-store";

/**
 * The apps a computer-use toast names and the open targets they bring: one
 * application is one chip and one open target.
 */

type ResourceRoute = NonNullable<CompactResourceDescriptor["route"]>;

/** App keys with a safe OS-level open target (mirrors openWorkToastTarget's allowlist). */
const APP_OPEN_URLS: Record<string, string> = {
  spotify: "spotify:",
};

/** Native app identities that also allow an exact bundle check and cold launch. */
const NATIVE_APP_BUNDLES: Record<string, string> = {
  calendar: "com.apple.iCal",
  mail: "com.apple.mail",
  notion: "notion.id",
  slack: "com.tinyspeck.slackmacgap",
  linear: "com.linear",
  figma: "com.figma.Desktop",
  outlook: "com.microsoft.Outlook",
  teams: "com.microsoft.teams2",
  safari: "com.apple.Safari",
  chrome: "com.google.Chrome",
  brave: "com.brave.Browser",
  edge: "com.microsoft.edgemac",
  arc: "company.thebrowser.Browser",
  dia: "company.thebrowser.dia",
  firefox: "org.mozilla.firefox",
};

export function computerOpenTargets(appKey: string, route: WorkToastAppRef["route"] = "ax"): WorkToastOpenTarget[] {
  const normalized = appKey.trim().toLowerCase();
  if (!normalized || normalized === "frontmost") return [];
  const url = APP_OPEN_URLS[normalized];
  if (url) return [{ appId: normalized, label: normalized, url }];
  if (route !== "ax") return [];
  const expectedBundleId = NATIVE_APP_BUNDLES[normalized];
  return [{ appId: normalized, label: normalized, kind: "app", route: "ax",
    ...(expectedBundleId ? { expectedBundleId } : {}) }];
}

export function appRef(resource: CompactResourceDescriptor, route: ResourceRoute): WorkToastAppRef {
  return { appId: resource.key.trim().toLowerCase(), route, confirmed: true, label: resource.label };
}

export type ToastApps = Pick<WorkToastItem, "apps" | "openTargets">;

/** The toast's apps and open targets with one app added: one application is
 *  one chip and one open target. A fill that arrives before its app frame
 *  holds the app under its lowercased display name (`fillAppRef`); the desktop
 *  ref that then names it by its real id takes that placeholder's place, open
 *  target included. Only desktop refs are matched by name, so a toolkit chip
 *  stays beside a native app that is called the same. */
export function withApp(item: ToastApps, incoming: WorkToastAppRef): ToastApps {
  const name = incoming.route === "ax" ? incoming.label?.trim().toLowerCase() : undefined;
  const placeholder = name !== incoming.appId && item.apps?.some((ref) => ref.route === "ax" && ref.appId === name)
    ? name : undefined;
  const apps = (item.apps ?? []).filter((ref) => ref.appId !== incoming.appId && ref.appId !== placeholder);
  // A destination with a uri is the server's, not the one the placeholder brought.
  const targets = (item.openTargets ?? []).filter((target) => target.appId !== placeholder || target.uri);
  const target = computerOpenTargets(incoming.appId, incoming.route)[0];
  return {
    apps: [...apps, incoming].slice(-4),
    openTargets: !target || targets.some((current) => current.appId === target.appId)
      ? targets : [...targets, target].slice(0, 4),
  };
}

/** A fill frame names its app by display name; app frames identify it by
 *  bundle id or executable stem. The desktop ref the toast already holds
 *  under that name keeps its id; without one the lowercased name stands in
 *  until `withApp` sees the real ref. */
export function fillAppRef(apps: WorkToastAppRef[] | undefined, appLabel: string): WorkToastAppRef {
  const name = appLabel.toLowerCase();
  const held = apps?.find((ref) => ref.route === "ax" && ref.label?.trim().toLowerCase() === name);
  return { ...held, appId: held?.appId ?? name, route: "ax", confirmed: true, label: held?.label ?? appLabel };
}

/** The toast's apps and open targets without one desktop app: a step whose
 *  first guess at its app was corrected takes back what the guess added. A
 *  server destination (one with a uri) stays. */
export function withoutApp(item: ToastApps, appId: string): ToastApps {
  return {
    apps: (item.apps ?? []).filter((ref) => ref.appId !== appId),
    openTargets: (item.openTargets ?? []).filter((target) => target.appId !== appId || target.uri),
  };
}
